// progressiveRecipeProcessor.js
// Handles batch processing of recipe details AFTER grocery matching
// New flow: recipes stream in → grocery matching → batch recipe details with real ingredients

import { fetchRecipeDetails, getRecipeImageWithRecovery, resetImageStagger, extractJsonStringField, extractJsonObjectField } from '../services/apiService';
import { getCurrentLanguage } from '../services/i18n';
import { createRecipeDetailsPrompt } from './recipeDetailsPromptBuilder';
import { normalizeRecipeFreshness } from './recipeFreshnessRules';
import { createCookingInstructionsRetryPrompt, validateCookingInstructions } from './recipeInstructionRules';

// Configuration
const MAX_CONCURRENT_RECIPE_DETAILS = 9; // Process all 9 recipes in parallel
let fallbackGenerationCounter = 0;

const createFallbackGenerationId = () => `processor-${Date.now()}-${++fallbackGenerationCounter}`;

// Track processing state
let processingState = {
  generationId: createFallbackGenerationId(),
  recipeDetailsQueue: [],
  activeRecipeDetails: 0,
  processedRecipes: new Set(),
  processingRecipes: new Set(),
  callbacks: null,
  isAborted: false,
  isHeld: true,      // Hold queue until Typesense matching completes
  userCountry: null, // Stored at queue time, used by releaseWithMatchedProducts
};

const getGenerationId = (options = {}) => options.generationId || processingState.generationId;

const isCurrentGeneration = (generationId) => generationId && processingState.generationId === generationId;

const isGenerationAborted = (generationId) => processingState.isAborted || !isCurrentGeneration(generationId);

/**
 * Abort all pending processing (call when user navigates away)
 */
export const abortProgressiveProcessor = (options = {}) => {
  const generationId = options.generationId;
  if (generationId && !isCurrentGeneration(generationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Ignoring abort for stale generation ${generationId}; current=${processingState.generationId}`);
    return;
  }

  console.log(`[ProgressiveProcessor] 🛑 Aborting pending processing for generation ${processingState.generationId}`);
  processingState.isAborted = true;
  processingState.recipeDetailsQueue = [];
  processingState.callbacks = null;
};

/**
 * Reset the processor state (call when starting a new meal plan generation)
 */
export const resetProgressiveProcessor = (options = {}) => {
  const generationId = options.generationId || createFallbackGenerationId();
  processingState = {
    generationId,
    recipeDetailsQueue: [],
    activeRecipeDetails: 0,
    processedRecipes: new Set(),
    processingRecipes: new Set(),
    callbacks: null,
    isAborted: false,
    isHeld: true,
    userCountry: null,
  };
  resetImageStagger();
  console.log(`[ProgressiveProcessor] 🔄 State reset for generation ${generationId}`);
};

/**
 * Initialize the processor with callbacks
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onRecipeDetailsStart - Called when recipe details fetch starts
 * @param {Function} callbacks.onRecipeDetailsComplete - Called when recipe details are ready
 * @param {Function} callbacks.onError - Called on errors
 */
export const initProgressiveProcessor = (callbacks, options = {}) => {
  const generationId = getGenerationId(options);
  if (!isCurrentGeneration(generationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Ignoring callbacks init for stale generation ${generationId}; current=${processingState.generationId}`);
    return;
  }
  processingState.callbacks = callbacks;
  console.log(`[ProgressiveProcessor] ✓ Initialized callbacks for generation ${generationId}`);
};

/**
 * Build ingredient context for a recipe based on matched grocery products.
 * Compares original recipe ingredients with matched store products to identify substitutions.
 * 
 * @param {Object} recipe - Recipe object with ingredients array
 * @param {Array} matchedProducts - Grocery list products array [{category, items: [...]}]
 * @returns {Object} { ingredientsWithContext: string, substitutions: Array }
 */
/**
 * Get the localized product name from a grocery item's lang map.
 * Falls back to displayName, then productName.
 */
const getLocalizedName = (item) => {
  const currentLang = getCurrentLanguage();
  let langMap = item?.lang;
  if (typeof langMap === 'string') {
    try { langMap = JSON.parse(langMap); } catch { langMap = null; }
  }
  if (langMap && typeof langMap === 'object') {
    const exact = langMap[currentLang];
    if (exact) return exact;
    const base = currentLang?.split('-')[0];
    if (base && langMap[base]) return langMap[base];
  }
  return item?.displayName || item?.productName || null;
};

const buildIngredientContext = (recipe, matchedProducts) => {
  if (!recipe?.ingredients || !matchedProducts?.length) {
    // Fallback: just use original ingredients
    const ingredientsText = Array.isArray(recipe?.ingredients) 
      ? recipe.ingredients.map(i => `${i.qty} ${i.product}`).join(', ')
      : 'No ingredients specified';
    return { ingredientsWithContext: ingredientsText, substitutions: [] };
  }

  // Flatten matched products into a lookup
  const allMatchedItems = matchedProducts.flatMap(cat => cat.items || []);
  
  const ingredientLines = [];
  const substitutions = [];
  const matchedNames = [];  // matched product names for image prompt

  for (const ing of recipe.ingredients) {
    const ingName = (ing.product || ing.name || '').toLowerCase().trim();
    
    // Find matching product - check if this ingredient appears in any matched product's usedIn or originalName
    const matchedItem = allMatchedItems.find(item => {
      if (!item) return false;
      // Check name/originalName against ingredient name
      const nameMatch = (item.name || '').toLowerCase().includes(ingName) || 
                        ingName.includes((item.name || '').toLowerCase()) ||
                        (item.originalName || '').toLowerCase().includes(ingName) ||
                        ingName.includes((item.originalName || '').toLowerCase()) ||
                        (item.allOriginals || []).some(orig => 
                          orig.toLowerCase().includes(ingName) || ingName.includes(orig.toLowerCase())
                        );
      return nameMatch;
    });

    if (matchedItem && matchedItem.productName) {
      const originalName = ing.product || ing.name;
      const matchedName = getLocalizedName(matchedItem) || matchedItem.productName;
      matchedNames.push(matchedItem.productName);

      // Detect if this is a substitution (product name differs significantly from original)
      const isSubstitution = matchedName.toLowerCase() !== originalName.toLowerCase() &&
                            !matchedName.toLowerCase().includes(originalName.toLowerCase()) &&
                            !originalName.toLowerCase().includes(matchedName.toLowerCase());
      
      if (isSubstitution) {
        ingredientLines.push(`${ing.qty} ${matchedName} (originally: ${originalName})`);
        substitutions.push({
          original: originalName,
          actual: matchedName,
          quantity: ing.qty
        });
      } else {
        ingredientLines.push(`${ing.qty} ${matchedName}`);
      }
    } else {
      // No match found - use original ingredient
      ingredientLines.push(`${ing.qty} ${ing.product || ing.name}`);
      matchedNames.push(ing.product || ing.name);  // fallback to original
    }
  }

  return {
    ingredientsWithContext: ingredientLines.join(', '),
    substitutions,
    matchedNames: matchedNames.filter(Boolean).join(', ')
  };
};

/**
 * Build the recipe details prompt with matched ingredient context
 * @param {Object} recipe - Recipe from meal plan
 * @param {Array} matchedProducts - Matched grocery products
 * @param {String} userCountry - User's country
 * @returns {String} Prompt for recipe details
 */
const buildRecipePromptWithContext = (recipe, matchedProducts, userCountry) => {
  return createRecipeDetailsPrompt(recipe, matchedProducts, { userCountry }).prompt;
};

/**
 * Process the next item in the recipe details queue
 */
const processNextRecipeDetails = async (userCountry, matchedProducts, generationId = processingState.generationId) => {
  if (isGenerationAborted(generationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Processing skipped for stale/aborted generation ${generationId}; current=${processingState.generationId}`);
    return;
  }

  if (processingState.isHeld) {
    return;
  }

  if (processingState.activeRecipeDetails >= MAX_CONCURRENT_RECIPE_DETAILS) {
    return;
  }
  
  const nextRecipe = processingState.recipeDetailsQueue.shift();
  if (!nextRecipe) {
    return;
  }

  const runGenerationId = nextRecipe._generationId || generationId;
  const recipeMatchedProducts = nextRecipe._matchedProducts || matchedProducts;

  if (isGenerationAborted(runGenerationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Dropping stale queued recipe "${nextRecipe.title}" for generation ${runGenerationId}`);
    return;
  }
  
  processingState.activeRecipeDetails++;
  processingState.processingRecipes.add(nextRecipe.title);
  
  const _t0 = Date.now();
  const _elapsed = () => ((Date.now() - _t0) / 1000).toFixed(2) + 's';
  console.log(`[ProgressiveProcessor] ▶ START "${nextRecipe.title}" (generation=${runGenerationId}, active: ${processingState.activeRecipeDetails}/${MAX_CONCURRENT_RECIPE_DETAILS}, minStorage=${nextRecipe.minStorageDays || 3}, matchedProducts=${recipeMatchedProducts?.length || 0} cats)`);
  
  if (!isGenerationAborted(runGenerationId) && processingState.callbacks?.onRecipeDetailsStart) {
    processingState.callbacks.onRecipeDetailsStart(nextRecipe);
  }
  
  try {
    const prompt = buildRecipePromptWithContext(nextRecipe, recipeMatchedProducts, userCountry);
    
    // Use matched store product names for image prompt (reflects actual purchased ingredients)
    const { matchedNames } = buildIngredientContext(nextRecipe, recipeMatchedProducts);
    const ingredientNames = matchedNames;

    console.log(`[ProgressiveProcessor] 📋 [${_elapsed()}] Prompt built for "${nextRecipe.title}" (${ingredientNames.split(', ').length} ingredients)`);

    // Early image generation: extract visual_description from streaming buffer
    let earlyImageFired = false;
    let _chunkCount = 0;
    let _titleExtractedAt = null;

    const onChunkForEarlyImage = (buffer) => {
      _chunkCount++;
      if (earlyImageFired || isGenerationAborted(runGenerationId)) return;

      // Track updated_title extraction moment (should arrive BEFORE visual_description)
      if (!_titleExtractedAt) {
        const earlyTitleCheck = extractJsonStringField(buffer, 'updated_title');
        if (earlyTitleCheck.complete) {
          _titleExtractedAt = Date.now();
          console.log(`[ProgressiveProcessor] 📝 [${_elapsed()}] updated_title extracted at chunk #${_chunkCount}: "${earlyTitleCheck.value?.trim()}" (original: "${nextRecipe.title}")`);
        }
      }

      const visResult = extractJsonStringField(buffer, 'visual_description');
      if (!visResult.complete) {
        // Fallback: AI sometimes generates visual_description as a JSON object instead of string
        const visObjResult = extractJsonObjectField(buffer, 'visual_description');
        if (!visObjResult.complete) return;

        earlyImageFired = true;
        const visualDescObj = visObjResult.value;

        const titleResult = extractJsonStringField(buffer, 'updated_title');
        const earlyTitle = (titleResult.complete && titleResult.value?.trim() && titleResult.value.trim() !== nextRecipe.title)
          ? titleResult.value.trim()
          : nextRecipe.title;

        const titleOrder = _titleExtractedAt ? `title first \u2713 (${((Date.now() - _titleExtractedAt) / 1000).toFixed(2)}s before visual_desc)` : 'title NOT extracted before visual_desc \u26a0\ufe0f';
        console.log(`[ProgressiveProcessor] \ud83d\ude80 [${_elapsed()}] visual_description extracted as OBJECT at chunk #${_chunkCount} \u2192 image gen START (${titleOrder})`);
        console.log(`[ProgressiveProcessor]    title="${earlyTitle}", visual_desc keys: ${Object.keys(visualDescObj).join(', ')}`);

        const stableOriginalTitle = nextRecipe.title;
        getRecipeImageWithRecovery(earlyTitle, ingredientNames, visualDescObj, {
          isAborted: () => isGenerationAborted(runGenerationId),
        })
          .then((imageURL) => {
            if (isGenerationAborted(runGenerationId)) return;
            if (imageURL && processingState.callbacks?.onImageComplete) {
              console.log(`[ProgressiveProcessor] \ud83d\uddbc\ufe0f [${_elapsed()}] Early image READY for "${earlyTitle}" (${imageURL.substring(0, 60)}...)`);
              processingState.callbacks.onImageComplete(earlyTitle, imageURL, stableOriginalTitle);
            } else if (!imageURL && processingState.callbacks?.onError) {
              console.warn(`[ProgressiveProcessor] \u26a0\ufe0f [${_elapsed()}] Early image returned null for "${earlyTitle}"`);
              processingState.callbacks.onError(earlyTitle, new Error('Image generation returned null'), stableOriginalTitle);
            }
          })
          .catch((err) => {
            if (isGenerationAborted(runGenerationId)) return;
            console.warn(`[ProgressiveProcessor] \u2717 [${_elapsed()}] Early image FAILED for "${earlyTitle}":`, err.message || err);
            if (processingState.callbacks?.onError) {
              processingState.callbacks.onError(earlyTitle, err, stableOriginalTitle);
            }
          });
        return;
      }

      earlyImageFired = true;
      const visualDesc = visResult.value;

      const titleResult = extractJsonStringField(buffer, 'updated_title');
      const earlyTitle = (titleResult.complete && titleResult.value?.trim() && titleResult.value.trim() !== nextRecipe.title)
        ? titleResult.value.trim()
        : nextRecipe.title;

      const titleOrder = _titleExtractedAt ? `title first ✓ (${((Date.now() - _titleExtractedAt) / 1000).toFixed(2)}s before visual_desc)` : 'title NOT extracted before visual_desc ⚠️';
      console.log(`[ProgressiveProcessor] 🚀 [${_elapsed()}] visual_description extracted at chunk #${_chunkCount} → image gen START (${titleOrder})`);
      console.log(`[ProgressiveProcessor]    title="${earlyTitle}", visual_desc=${visualDesc.length} chars`);

      const stableOriginalTitle = nextRecipe.title;
      getRecipeImageWithRecovery(earlyTitle, ingredientNames, visualDesc, {
        isAborted: () => isGenerationAborted(runGenerationId),
      })
        .then((imageURL) => {
          if (isGenerationAborted(runGenerationId)) return;
          if (imageURL && processingState.callbacks?.onImageComplete) {
            console.log(`[ProgressiveProcessor] 🖼️ [${_elapsed()}] Early image READY for "${earlyTitle}" (${imageURL.substring(0, 60)}...)`);
            processingState.callbacks.onImageComplete(earlyTitle, imageURL, stableOriginalTitle);
          } else if (!imageURL && processingState.callbacks?.onError) {
            console.warn(`[ProgressiveProcessor] ⚠️ [${_elapsed()}] Early image returned null for "${earlyTitle}"`);
            processingState.callbacks.onError(earlyTitle, new Error('Image generation returned null'), stableOriginalTitle);
          }
        })
        .catch((err) => {
          if (isGenerationAborted(runGenerationId)) return;
          console.warn(`[ProgressiveProcessor] ✗ [${_elapsed()}] Early image FAILED for "${earlyTitle}":`, err.message || err);
          if (processingState.callbacks?.onError) {
            processingState.callbacks.onError(earlyTitle, err, stableOriginalTitle);
          }
        });
    };

    let instructions = await fetchRecipeDetails(
      prompt,
      onChunkForEarlyImage,
      null,
      {
        title: nextRecipe.title,
        description: nextRecipe.description,
        autoGenerateImage: false,
      }
    );
    
    if (isGenerationAborted(runGenerationId)) {
      console.log(`[ProgressiveProcessor] ⏹️ Ignoring details for "${nextRecipe.title}" - stale/aborted generation ${runGenerationId}`);
      return;
    }

    let instructionValidation = validateCookingInstructions(instructions);
    if (!instructionValidation.isValid) {
      console.warn(`[ProgressiveProcessor] ⚠️ [${_elapsed()}] Invalid cooking instructions for "${nextRecipe.title}": ${instructionValidation.issues.join('; ')}. Retrying once.`);
      instructions = await fetchRecipeDetails(
        createCookingInstructionsRetryPrompt(prompt, instructionValidation.issues),
        onChunkForEarlyImage,
        null,
        {
          title: nextRecipe.title,
          description: nextRecipe.description,
          autoGenerateImage: false,
        }
      );

      if (isGenerationAborted(runGenerationId)) {
        console.log(`[ProgressiveProcessor] ⏹️ Ignoring retry details for "${nextRecipe.title}" - stale/aborted generation ${runGenerationId}`);
        return;
      }

      instructionValidation = validateCookingInstructions(instructions);
      if (!instructionValidation.isValid) {
        console.warn(`[ProgressiveProcessor] ⚠️ [${_elapsed()}] Cooking instructions still imperfect for "${nextRecipe.title}": ${instructionValidation.issues.join('; ')}`);
        if (instructions && typeof instructions === 'object') {
          instructions._instructionQualityIssues = instructionValidation.issues;
        }
      } else {
        console.log(`[ProgressiveProcessor] ✓ [${_elapsed()}] Cooking instructions repaired for "${nextRecipe.title}"`);
      }
    }
    
    console.log(`[ProgressiveProcessor] 📥 [${_elapsed()}] Stream complete for "${nextRecipe.title}" (${_chunkCount} chunks, earlyImageFired=${earlyImageFired})`);
    
    // Post-process storage/freshness so fish stays truthful at 2 days while late-slot fish moves to freezer.
    const minDays = nextRecipe.minStorageDays || 3;
    normalizeRecipeFreshness(instructions, {
      minStorageDays: minDays,
      language: getCurrentLanguage(),
      logger: (message) => console.log(`[ProgressiveProcessor] 📊 ${message}`),
    });
    
    // Log parsed response summary
    if (instructions?._partial) {
      console.warn(`[ProgressiveProcessor] ⚠️ [${_elapsed()}] PARTIAL/fallback data for "${nextRecipe.title}"`);
    } else {
      console.log(`[ProgressiveProcessor] ✓ [${_elapsed()}] Full response parsed: title="${instructions?.updated_title || '(unchanged)'}", cooking_time=${instructions?.cooking_time || '?'}min, storage=${instructions?.max_storage_time || '?'}d, steps=${instructions?.cooking_instructions?.length || 0}, ingredients_storage=${instructions?.ingredient_storage?.length || 0}`);
    }

    processingState.processedRecipes.add(nextRecipe.title);
    
    // Also track the updated title if AI corrected it
    if (instructions?.updated_title && instructions.updated_title.trim() !== nextRecipe.title) {
      processingState.processedRecipes.add(instructions.updated_title.trim());
    }
    
    if (processingState.callbacks?.onRecipeDetailsComplete) {
      processingState.callbacks.onRecipeDetailsComplete(nextRecipe.title, instructions);
    }

    // Generate image from visual_description (fire-and-forget, runs parallel to other recipes)
    // Skip if early image generation already started during streaming
    const visualDesc = instructions?.visual_description;
    const finalTitle = (instructions?.updated_title && instructions.updated_title.trim() !== nextRecipe.title)
      ? instructions.updated_title.trim()
      : nextRecipe.title;

    if (earlyImageFired) {
      console.log(`[ProgressiveProcessor] ✓ [${_elapsed()}] Image already started early for "${finalTitle}" — skipping post-stream generation`);
    } else if (visualDesc) {
      console.log(`[ProgressiveProcessor] 🖼️ [${_elapsed()}] Post-stream image gen START for "${finalTitle}" (earlyImageFired=${earlyImageFired})`);
      getRecipeImageWithRecovery(finalTitle, ingredientNames, visualDesc, {
        isAborted: () => isGenerationAborted(runGenerationId),
      })
        .then((imageURL) => {
          if (isGenerationAborted(runGenerationId)) return;
          if (imageURL && processingState.callbacks?.onImageComplete) {
            console.log(`[ProgressiveProcessor] 🖼️ [${_elapsed()}] Post-stream image READY for "${finalTitle}" (${imageURL.substring(0, 60)}...)`);
            processingState.callbacks.onImageComplete(finalTitle, imageURL, nextRecipe.title);
          } else if (!imageURL && processingState.callbacks?.onError) {
            console.warn(`[ProgressiveProcessor] ⚠️ [${_elapsed()}] Post-stream image returned null for "${finalTitle}"`);
            processingState.callbacks.onError(finalTitle, new Error('Image generation returned null'), nextRecipe.title);
          }
        })
        .catch((err) => {
          if (isGenerationAborted(runGenerationId)) return;
          console.warn(`[ProgressiveProcessor] ✗ [${_elapsed()}] Post-stream image FAILED for "${finalTitle}":`, err.message || err);
          if (processingState.callbacks?.onError) {
            processingState.callbacks.onError(finalTitle, err, nextRecipe.title);
          }
        });
    } else {
      // No visual description available — clear loading state
      console.warn(`[ProgressiveProcessor] ⚠️ [${_elapsed()}] No visual_description for "${nextRecipe.title}" — skipping image`);
      if (processingState.callbacks?.onError) {
        processingState.callbacks.onError(nextRecipe.title, new Error('No visual_description available'));
      }
    }
    
    console.log(`[ProgressiveProcessor] ■ [${_elapsed()}] DONE "${finalTitle}" — earlyImage=${earlyImageFired}, titleChanged=${finalTitle !== nextRecipe.title}`);

  } catch (error) {
    if (!isGenerationAborted(runGenerationId)) {
      console.error(`[ProgressiveProcessor] ✗ [${_elapsed()}] ERROR for "${nextRecipe.title}":`, error.message || error);
      
      if (processingState.callbacks?.onError) {
        processingState.callbacks.onError(nextRecipe.title, error);
      }
    }
  } finally {
    if (isCurrentGeneration(runGenerationId)) {
      processingState.activeRecipeDetails = Math.max(0, processingState.activeRecipeDetails - 1);
      processingState.processingRecipes.delete(nextRecipe.title);

      // Process next item in queue
      processNextRecipeDetails(userCountry, matchedProducts, runGenerationId);
    } else {
      console.log(`[ProgressiveProcessor] ⏹️ Stale finally ignored for "${nextRecipe.title}" generation ${runGenerationId}; current=${processingState.generationId}`);
    }
  }
};

/**
 * Queue a single recipe for processing with matched products context.
 * Used for replacement flow (dislike/alternative).
 * 
 * @param {Object} recipe - The recipe object
 * @param {String} userCountry - User's country
 * @param {Array} matchedProducts - Matched grocery products
 */
export const queueRecipeForProcessing = (recipe, userCountry, matchedProducts, options = {}) => {
  const generationId = getGenerationId(options);

  if (isGenerationAborted(generationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Ignoring queue request for "${recipe?.title}" - stale/aborted generation ${generationId}; current=${processingState.generationId}`);
    return;
  }
  
  if (!recipe || !recipe.title) {
    console.warn('[ProgressiveProcessor] Invalid recipe provided');
    return;
  }
  
  if (processingState.processedRecipes.has(recipe.title) || 
      processingState.processingRecipes.has(recipe.title)) {
    console.log(`[ProgressiveProcessor] Skipping "${recipe.title}" - already processed/processing`);
    return;
  }
  
  if (processingState.recipeDetailsQueue.some(r => r.title === recipe.title)) {
    return;
  }
  
  if (!processingState.userCountry && userCountry) {
    processingState.userCountry = userCountry;
  }

  console.log(`[ProgressiveProcessor] 📥 Queuing "${recipe.title}" for processing (generation=${generationId}, held=${processingState.isHeld}, queue size: ${processingState.recipeDetailsQueue.length + 1})`);

  processingState.recipeDetailsQueue.push({ ...recipe, _generationId: generationId, _matchedProducts: matchedProducts });
  processNextRecipeDetails(userCountry, matchedProducts, generationId);
};

/**
 * Release the held recipe queue with full consolidated Typesense match data.
 * Called once after grocery Phase 2 (Typesense matching) completes.
 * Updates all queued recipes with real product context, then fires all in parallel.
 *
 * @param {Array} matchedProducts - Grouped [{category, items:[...]}] from grocery Phase 2
 * @param {Object} options
 */
export const releaseWithMatchedProducts = (matchedProducts, options = {}) => {
  const generationId = getGenerationId(options);
  if (isGenerationAborted(generationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Ignoring release for stale/aborted generation ${generationId}; current=${processingState.generationId}`);
    return;
  }

  const queuedCount = processingState.recipeDetailsQueue.length;
  console.log(`[ProgressiveProcessor] 🔓 Releasing ${queuedCount} queued recipes with ${matchedProducts?.length || 0} product categories (generation=${generationId})`);

  // Stamp every waiting recipe with the full consolidated match data
  processingState.recipeDetailsQueue = processingState.recipeDetailsQueue.map(recipe => ({
    ...recipe,
    _matchedProducts: matchedProducts,
  }));

  processingState.isHeld = false;

  const country = processingState.userCountry;
  const concurrentStarts = Math.min(queuedCount, MAX_CONCURRENT_RECIPE_DETAILS);
  for (let i = 0; i < concurrentStarts; i++) {
    processNextRecipeDetails(country, matchedProducts, generationId);
  }
};

/**
 * Queue ALL recipes for batch processing after grocery matching is complete.
 * This is the main entry point in the new flow.
 * 
 * @param {Array} recipes - All recipes from the meal plan
 * @param {String} userCountry - User's country
 * @param {Array} matchedProducts - Matched grocery products from grocery processor
 */
export const queueAllRecipesForProcessing = (recipes, userCountry, matchedProducts, options = {}) => {
  const generationId = getGenerationId(options);

  if (isGenerationAborted(generationId)) {
    console.log(`[ProgressiveProcessor] ⏹️ Ignoring batch queue for stale/aborted generation ${generationId}; current=${processingState.generationId}`);
    return;
  }
  
  if (!recipes || recipes.length === 0) {
    console.warn('[ProgressiveProcessor] No recipes to process');
    return;
  }
  
  console.log(`[ProgressiveProcessor] 📦 Batch queuing ${recipes.length} recipes for processing with ${matchedProducts?.length || 0} product categories (generation=${generationId})`);
  
  for (let i = 0; i < recipes.length; i++) {
    const recipe = recipes[i];
    if (!recipe || !recipe.title || recipe.isPlaceholder) continue;
    
    if (processingState.processedRecipes.has(recipe.title) || 
        processingState.processingRecipes.has(recipe.title) ||
        processingState.recipeDetailsQueue.some(r => r.title === recipe.title)) {
      continue;
    }
    
    // Calculate minimum storage days based on position within meal type
    // Meals are ordered: B,B,B,L,L,L,D,D,D — within each type, eaten sequentially over ~5 days
    // 1st of each type (index%3=0): cooked first → minStorageDays = 3
    // 2nd of each type (index%3=1): cooked mid-week → minStorageDays = 4
    // 3rd of each type (index%3=2): cooked last → minStorageDays = 5
    const positionInMealType = i % 3; // 0, 1, 2
    const minStorageDays = 3 + positionInMealType; // 3, 4, 5
    
    console.log(`[ProgressiveProcessor] 📊 Recipe "${recipe.title}" position ${i} (${positionInMealType + 1}/${3} in ${recipe.mealType}) → minStorageDays: ${minStorageDays}`);
    
    processingState.recipeDetailsQueue.push({ ...recipe, minStorageDays, _generationId: generationId, _matchedProducts: matchedProducts });
  }
  
  // Start processing all queued recipes (respects MAX_CONCURRENT_RECIPE_DETAILS)
  const concurrentStarts = Math.min(processingState.recipeDetailsQueue.length, MAX_CONCURRENT_RECIPE_DETAILS);
  for (let i = 0; i < concurrentStarts; i++) {
    processNextRecipeDetails(userCountry, matchedProducts, generationId);
  }
};

/**
 * Get current processing status
 */
export const getProcessingStatus = () => ({
  generationId: processingState.generationId,
  queueLength: processingState.recipeDetailsQueue.length,
  activeRecipeDetails: processingState.activeRecipeDetails,
  processedCount: processingState.processedRecipes.size,
  processingCount: processingState.processingRecipes.size
});

/**
 * Check if a recipe has been processed
 */
export const isRecipeProcessed = (recipeTitle) => {
  return processingState.processedRecipes.has(recipeTitle);
};

/**
 * Check if a recipe is currently being processed
 */
export const isRecipeProcessing = (recipeTitle) => {
  return processingState.processingRecipes.has(recipeTitle);
};
